import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Plus, X, Check, Link2, Undo2, Redo2, ZoomIn, ZoomOut, RotateCcw, Paperclip, FileText, Image as ImageIcon, Trash2, UploadCloud, Loader2 } from 'lucide-react';
import { API_URL } from '../config/api';
import './PedigreeTree.css';

const generateId = () => `n_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

const PedigreeTree = ({ patientId }) => {
    const [pedigree, setPedigree] = useState({ members: [], connections: [] });
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showFilesPanel, setShowFilesPanel] = useState(false);
    const [pedigreeFiles, setPedigreeFiles] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [dragging, setDragging] = useState(null);
    const [connectMode, setConnectMode] = useState(false);
    const [connectFrom, setConnectFrom] = useState(null);
    const [tooltip, setTooltip] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);
    const [panning, setPanning] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [saveState, setSaveState] = useState('idle');
    const tooltipTimer = useRef(null);
    const saveTimerRef = useRef(null);
    const activeSaveControllerRef = useRef(null);
    const pendingPedigreeRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '', age: '', sex: 'M', status: 'alive', causeOfDeath: '',
        affected: false, carrier: false, isProband: false, notes: ''
    });
    const svgRef = useRef(null);

    const getPrefsKey = () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return `pedigree_prefs_${user.id || 'default'}`;
    };

    useEffect(() => {
        const prefs = JSON.parse(localStorage.getItem(getPrefsKey()) || '{}');
        if (prefs.zoom) setZoom(prefs.zoom);
        if (prefs.panX) setPanX(prefs.panX);
        if (prefs.panY) setPanY(prefs.panY);
    }, []);

    const savePrefs = (z, px, py) => {
        localStorage.setItem(getPrefsKey(), JSON.stringify({ zoom: z, panX: px, panY: py }));
    };

    useEffect(() => {
        fetchPedigree();
        fetchPedigreeFiles();
    }, [patientId]);

    const fetchPedigree = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/patients/${patientId}/genetic-profile`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.pedigree_data && data.pedigree_data.members && data.pedigree_data.members.length > 0) {
                    const loadedPedigree = { members: data.pedigree_data.members || [], connections: data.pedigree_data.connections || [] };
                    setPedigree(loadedPedigree);
                    setHistory([loadedPedigree]);
                    setHistoryIndex(0);
                } else {
                    setPedigree({ members: [], connections: [] });
                    setHistory([{ members: [], connections: [] }]);
                    setHistoryIndex(0);
                }
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const savePedigreeNow = useCallback(async (p) => {
        try {
            if (!p) return;
            if (activeSaveControllerRef.current) {
                activeSaveControllerRef.current.abort();
            }

            const controller = new AbortController();
            activeSaveControllerRef.current = controller;
            setSaveState('saving');

            const token = localStorage.getItem('token');
            const payload = new FormData();
            payload.append('patient_id', patientId);
            payload.append('pedigree_data', JSON.stringify(p));

            const response = await fetch(`${API_URL}/genetic-profiles`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: payload,
                signal: controller.signal
            });

            if (!response.ok) {
                let apiMessage = `Error al guardar arbol (${response.status})`;
                try {
                    const errorBody = await response.json();
                    if (errorBody && errorBody.message) {
                        apiMessage = errorBody.message;
                    }
                } catch (parseError) {
                    // Ignorar errores de parseo para mantener mensaje base.
                }
                throw new Error(apiMessage);
            }

            setSaveState('saved');
            window.setTimeout(() => {
                setSaveState((prev) => (prev === 'saved' ? 'idle' : prev));
            }, 1500);
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error(err);
            setSaveState('error');
            alert(err.message || 'No se pudo guardar el arbol.');
        }
    }, [patientId]);

    const queueSave = useCallback((p, immediate = false) => {
        pendingPedigreeRef.current = p;

        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }

        if (immediate) {
            savePedigreeNow(pendingPedigreeRef.current);
            return;
        }

        saveTimerRef.current = window.setTimeout(() => {
            savePedigreeNow(pendingPedigreeRef.current);
        }, 900);
    }, [savePedigreeNow]);

    useEffect(() => {
        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
            if (activeSaveControllerRef.current) {
                activeSaveControllerRef.current.abort();
            }
        };
    }, []);

    const parseJsonSafe = async (response) => {
        const text = await response.text();
        if (!text) return {};
        try {
            return JSON.parse(text);
        } catch (_err) {
            const cleaned = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
            return { message: cleaned || 'Respuesta invalida del servidor' };
        }
    };

    const resolveFileUrl = (filePath) => {
        if (!filePath) return null;
        const path = String(filePath).trim();
        if (/^https?:\/\//i.test(path)) return path;
        const normalized = path.replace(/\\/g, '/');
        const withLeadingSlash = normalized.startsWith('/') ? normalized : `/${normalized}`;
        return `${API_URL}${withLeadingSlash}`;
    };

    const fetchPedigreeFiles = async () => {
        try {
            setLoadingFiles(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/patients/${patientId}/pedigree-files`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await parseJsonSafe(response);
                setPedigreeFiles(Array.isArray(data) ? data : []);
            } else {
                const errorData = await parseJsonSafe(response);
                console.error("Error al obtener adjuntos", errorData.message || response.status);
            }
        } catch (err) { console.error("Error fetching pedigree files", err); }
        finally { setLoadingFiles(false); }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validar tamaño 50MB
        if (file.size > 50 * 1024 * 1024) {
            alert("El archivo excede el límite de 50MB.");
            return;
        }

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert("Solo se admiten PDF o Imágenes.");
            return;
        }

        try {
            setUploadingFile(true);
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_URL}/patients/${patientId}/pedigree-files`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (response.ok) {
                const newFile = await parseJsonSafe(response);
                if (newFile && newFile.id) {
                    setPedigreeFiles(prev => [newFile, ...prev]);
                } else {
                    await fetchPedigreeFiles();
                }
            } else {
                const errorData = await parseJsonSafe(response);
                alert(errorData.message || "Error al subir el archivo");
            }
        } catch (err) {
            console.error("Upload error", err);
            alert("Ocurrió un error al subir el archivo.");
        } finally {
            setUploadingFile(false);
            e.target.value = ''; // Reset input
        }
    };

    const handleDeleteFile = async (fileId) => {
        if (!window.confirm("¿Seguro que desea eliminar este archivo?")) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/pedigree-files/${fileId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setPedigreeFiles(prev => prev.filter(f => f.id !== fileId));
            } else {
                const errorData = await parseJsonSafe(response);
                alert(errorData.message || "Error al eliminar el archivo");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const addToHistory = (p) => {
        const h = history.slice(0, historyIndex + 1);
        h.push(JSON.parse(JSON.stringify(p)));
        setHistory(h);
        setHistoryIndex(h.length - 1);
    };

    const handleUndo = () => { if (historyIndex > 0) { setHistoryIndex(historyIndex - 1); setPedigree(history[historyIndex - 1]); queueSave(history[historyIndex - 1]); } };
    const handleRedo = () => { if (historyIndex < history.length - 1) { setHistoryIndex(historyIndex + 1); setPedigree(history[historyIndex + 1]); queueSave(history[historyIndex + 1]); } };
    const handleZoomIn = () => { const z = Math.min(zoom + 0.2, 2); setZoom(z); savePrefs(z, panX, panY); };
    const handleZoomOut = () => { const z = Math.max(zoom - 0.2, 0.4); setZoom(z); savePrefs(z, panX, panY); };
    const handleZoomReset = () => { setZoom(1); setPanX(0); setPanY(0); savePrefs(1, 0, 0); };

    const openAddModal = () => { setEditingMember(null); setFormData({ name: '', age: '', sex: 'M', status: 'alive', causeOfDeath: '', affected: false, carrier: false, isProband: false, notes: '', x: 300, y: 240 }); setShowModal(true); };
    const openEditModal = (m) => { setEditingMember(m); setFormData({ ...m }); setShowModal(true); };

    const handleSave = () => {
        if (!formData.name || !String(formData.name).trim()) {
            alert('El nombre del familiar es obligatorio.');
            return;
        }

        const d = { ...formData, id: editingMember ? editingMember.id : generateId(), x: formData.x || 300, y: formData.y || 225 };
        let members = editingMember ? pedigree.members.map(m => m.id === editingMember.id ? d : m) : [...pedigree.members, d];
        if (d.isProband) {
            members = members.map((m) => (m.id === d.id ? m : { ...m, isProband: false }));
        }
        const p = { ...pedigree, members };
        setPedigree(p); addToHistory(p); queueSave(p); setShowModal(false);
    };

    const handleDelete = (id) => {
        if (window.confirm('¿Eliminar?')) {
            const members = pedigree.members.filter(m => m.id !== id);
            const connections = (pedigree.connections || []).filter(c => c.from !== id && c.to !== id);
            const p = { members, connections };
            setPedigree(p); addToHistory(p); queueSave(p);
            setTooltip(null); // Clear any ghost tooltips
        }
    };

    const handleMouseDown = (e, member) => {
        if (connectMode) return;
        e.stopPropagation();
        const svg = svgRef.current;
        const pt = svg.createSVGPoint();
        pt.x = e.clientX; pt.y = e.clientY;
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
        setDragging({ id: member.id, offsetX: svgP.x - member.x, offsetY: svgP.y - member.y });
    };

    const handleMouseMove = useCallback((e) => {
        if (dragging && svgRef.current) {
            const svg = svgRef.current;
            const pt = svg.createSVGPoint();
            pt.x = e.clientX; pt.y = e.clientY;
            const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
            const SNAP = 30; // 30px Snap to Grid for clinical accuracy
            const newX = Math.round(Math.max(30, Math.min(870, svgP.x - dragging.offsetX)) / SNAP) * SNAP;
            const newY = Math.round(Math.max(30, Math.min(570, svgP.y - dragging.offsetY)) / SNAP) * SNAP;
            setPedigree(prev => ({ ...prev, members: prev.members.map(m => m.id === dragging.id ? { ...m, x: newX, y: newY } : m) }));
        }
        if (panning) {
            const dx = (e.clientX - panning.startX) / zoom;
            const dy = (e.clientY - panning.startY) / zoom;
            setPanX(Math.max(-150, Math.min(400, panning.origPanX - dx)));
            setPanY(Math.max(-100, Math.min(300, panning.origPanY - dy)));;
        }
    }, [dragging, panning, zoom]);

    const handleMouseUp = useCallback(() => {
        if (dragging) { addToHistory(pedigree); queueSave(pedigree); setDragging(null); }
        if (panning) { savePrefs(zoom, panX, panY); setPanning(null); }
    }, [dragging, panning, pedigree, zoom, panX, panY, queueSave]);

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    }, [handleMouseMove, handleMouseUp]);

    const handleNodeClick = (m) => {
        if (!connectMode) return;
        if (!connectFrom) { setConnectFrom(m.id); }
        else if (connectFrom !== m.id) {
            const fromMember = getMember(connectFrom);
            const toMember = m;
            let finalFrom = connectFrom;
            let finalTo = m.id;
            if (fromMember && toMember && fromMember.y > toMember.y) {
                finalFrom = m.id;
                finalTo = connectFrom;
            }
            const exists = (pedigree.connections || []).some((c) => (c.from === finalFrom && c.to === finalTo) || (c.from === finalTo && c.to === finalFrom));
            if (exists) {
                setConnectFrom(null);
                setConnectMode(false);
                return;
            }
            const p = { ...pedigree, connections: [...(pedigree.connections || []), { from: finalFrom, to: finalTo, type: 'descent' }] };
            setPedigree(p); addToHistory(p); queueSave(p); setConnectFrom(null); setConnectMode(false);
        } else { setConnectFrom(null); }
    };

    const removeConnection = (idx) => {
        if (window.confirm('¿Eliminar conexión?')) {
            const p = { ...pedigree, connections: (pedigree.connections || []).filter((_, i) => i !== idx) };
            setPedigree(p); addToHistory(p); queueSave(p);
        }
    };

    const toggleConnectMode = () => { setConnectMode(!connectMode); setConnectFrom(null); };
    const handleMouseEnter = (m, e) => { tooltipTimer.current = setTimeout(() => setTooltip({ member: m, x: e.clientX, y: e.clientY }), 300); };
    const handleMouseLeave = () => { if (tooltipTimer.current) { clearTimeout(tooltipTimer.current); } setTooltip(null); };
    const startPan = (e) => { e.preventDefault(); setPanning({ startX: e.clientX, startY: e.clientY, origPanX: panX, origPanY: panY }); };
    const getMember = (id) => pedigree.members.find(m => m.id === id);

    const renderSymbol = (m) => {
        const size = 28, half = 14;
        let shape;
        if (m.status === 'aborted') shape = <polygon points={`-10,10 0,-10 10,10`} fill={m.affected ? '#000' : 'white'} stroke="#000" strokeWidth="2" />;
        else if (m.sex === 'M') shape = <rect x={-half} y={-half} width={size} height={size} fill={m.affected ? '#000' : 'white'} stroke="#000" strokeWidth="2" />;
        else if (m.sex === 'F') shape = <circle cx={0} cy={0} r={half} fill={m.affected ? '#000' : 'white'} stroke="#000" strokeWidth="2" />;
        else shape = <polygon points={`0,-${half} ${half},0 0,${half} -${half},0`} fill={m.affected ? '#000' : 'white'} stroke="#000" strokeWidth="2" />;
        return (<g>{shape}{m.carrier && !m.affected && <circle cx={0} cy={0} r={6} fill="#000" />}{m.status === 'deceased' && <line x1={-18} y1={18} x2={18} y2={-18} stroke="#000" strokeWidth="2" />}{m.isProband && <path d={`M-26,0 L-18,0 M-22,-4 L-18,0 L-22,4`} stroke="#000" strokeWidth="2" fill="none" />}</g>);
    };

    const handleCoupleClick = (idx) => {
        if (connectMode) {
            const conn = pedigree.connections[idx];
            const coupleId = [conn.from, conn.to].sort().join('-');
            if (!connectFrom) {
                setConnectFrom(coupleId);
            } else {
                setConnectFrom(null);
            }
        } else {
            // Toggle consanguinidad (línea doble)
            const p = { ...pedigree, connections: [...pedigree.connections] };
            p.connections[idx] = { ...p.connections[idx], consanguineous: !p.connections[idx].consanguineous };
            setPedigree(p); addToHistory(p); queueSave(p);
        }
    };

    const renderConnections = () => {
        const connections = pedigree.connections || [];
        const elements = [];
        const coupleConns = [];

        connections.forEach((c, i) => {
            if (c.from.includes('-')) return; // Skip couple-descent entries for now
            const from = getMember(c.from);
            const to = getMember(c.to);
            if (!from || !to) return;
            // Force strict horizontal couple recognition
            if (Math.abs(from.y - to.y) <= 30) {
                coupleConns.push({ ...c, fromNode: from, toNode: to, idx: i, coupleId: [from.id, to.id].sort().join('-') });
            }
        });

        // Loop over couples
        coupleConns.forEach((cc) => {
            const fromX = Math.min(cc.fromNode.x, cc.toNode.x) + 16;
            const toX = Math.max(cc.fromNode.x, cc.toNode.x) - 16;
            const avgY = (cc.fromNode.y + cc.toNode.y) / 2;
            const isSelected = connectFrom === cc.coupleId;
            const color = isSelected ? '#2563eb' : '#000';

            if (cc.consanguineous) { // Consanguinity double line
                elements.push(<line key={`c1-${cc.idx}`} x1={fromX} y1={avgY - 3} x2={toX} y2={avgY - 3} stroke={color} strokeWidth="1.5" />);
                elements.push(<line key={`c2-${cc.idx}`} x1={fromX} y1={avgY + 3} x2={toX} y2={avgY + 3} stroke={color} strokeWidth="1.5" />);
            } else {
                elements.push(<line key={`c-${cc.idx}`} x1={fromX} y1={avgY} x2={toX} y2={avgY} stroke={color} strokeWidth="1.5" />);
            }

            // Union deletion handle (X button)
            const midX = (fromX + toX) / 2;
            elements.push(
                <g key={`handle-${cc.idx}`} className="conn-handle" onClick={(e) => { e.stopPropagation(); handleCoupleClick(cc.idx); }}>
                    <line x1={fromX} y1={avgY} x2={toX} y2={avgY} stroke="transparent" strokeWidth="15" style={{ cursor: 'pointer' }} />
                    <circle cx={midX} cy={avgY} r={10} className="handle-bg" />
                    <text x={midX} y={avgY + 3.5} textAnchor="middle" className="handle-icon" fontSize="10">{cc.consanguineous ? '=' : '+'}</text>
                    <g className="delete-trigger" onClick={(e) => { e.stopPropagation(); removeConnection(cc.idx); }}>
                        <circle cx={midX + 18} cy={avgY} r={8} className="delete-bg" />
                        <text x={midX + 18} y={avgY + 3} textAnchor="middle" className="delete-icon" fontSize="9">×</text>
                    </g>
                    <title>{cc.consanguineous ? 'Quitar consanguinidad' : 'Marcar consanguinidad / Click en + para cambiar'}</title>
                </g>
            );
        });

        const descentConns = [];
        connections.forEach((c, i) => {
            if (c.from.includes('-')) {
                // Connection originating from a Union (Half-Siblings support)
                const to = getMember(c.to);
                if (to) descentConns.push({ coupleId: c.from, child: to, idx: i });
            } else {
                // Legacy / single-parent connection backward compat
                const from = getMember(c.from);
                const to = getMember(c.to);
                if (from && to && from.y < to.y - 30) {
                    // Try to find a couple for this parent, but only snap to it if the child is placed "under" that couple's influence area
                    const parentCouples = coupleConns.filter(cc => cc.fromNode.id === from.id || cc.toNode.id === from.id);
                    let coupleId = null;
                    if (parentCouples.length > 0) {
                        // Find the couple whose center is closest to the child's X position
                        const closestCouple = parentCouples.reduce((prev, curr) => {
                            const currCenter = (curr.fromNode.x + curr.toNode.x) / 2;
                            const prevCenter = (prev.fromNode.x + prev.toNode.x) / 2;
                            return Math.abs(currCenter - to.x) < Math.abs(prevCenter - to.x) ? curr : prev;
                        });

                        const closestCenter = (closestCouple.fromNode.x + closestCouple.toNode.x) / 2;
                        // Only snap to the couple if the child is placed near the couple's X-axis space (within 60px of center)
                        // Otherwise, treat it as a true single-parent line (like in the user's screenshot for hijo3)
                        if (Math.abs(closestCenter - to.x) < 90 || to.x > Math.min(closestCouple.fromNode.x, closestCouple.toNode.x) && to.x < Math.max(closestCouple.fromNode.x, closestCouple.toNode.x)) {
                            coupleId = closestCouple.coupleId;
                        }
                    }
                    descentConns.push({ parentId: from.id, coupleId, child: to, idx: i });
                }
            }
        });

        const childrenByGroup = {};
        descentConns.forEach(dc => {
            const key = dc.coupleId || dc.parentId;
            if (!childrenByGroup[key]) childrenByGroup[key] = { conns: [], coupleId: dc.coupleId, parentId: dc.parentId };
            childrenByGroup[key].conns.push(dc);
        });

        Object.values(childrenByGroup).forEach(group => {
            const { conns, coupleId, parentId } = group;
            if (conns.length === 0) return;
            conns.sort((a, b) => a.child.x - b.child.x);

            let dropX, dropY;
            if (coupleId) {
                const cc = coupleConns.find(c => c.coupleId === coupleId);
                if (cc) {
                    dropX = (cc.fromNode.x + cc.toNode.x) / 2;
                    dropY = (cc.fromNode.y + cc.toNode.y) / 2;
                } else {
                    dropX = conns[0].child.x;
                    dropY = conns[0].child.y - 30; // Broken couple fallback
                }
            } else if (parentId) {
                const parent = getMember(parentId);
                dropX = parent.x;
                dropY = parent.y + 16;
            }

            const childY = conns[0].child.y;
            const midY = dropY + (childY - dropY) * 0.5;

            // Descent trunk line
            elements.push(<line key={`drop-${dropX}-${midY}`} x1={dropX} y1={dropY} x2={dropX} y2={midY} stroke="#000" strokeWidth="1.5" />);

            // Sibship horizontal bar
            const leftX = Math.min(dropX, conns[0].child.x);
            const rightX = Math.max(dropX, conns[conns.length - 1].child.x);
            elements.push(<line key={`sibbar-${midY}-${leftX}`} x1={leftX} y1={midY} x2={rightX} y2={midY} stroke="#000" strokeWidth="1.5" />);

            // Descender branches and deletion handles
            conns.forEach((dc) => {
                elements.push(<line key={`to-${dc.child.id}`} x1={dc.child.x} y1={midY} x2={dc.child.x} y2={dc.child.y - 16} stroke="#000" strokeWidth="1.5" className="connection-line" />);

                // Visible X handle for descent lines
                const handleY = (midY + dc.child.y - 16) * 0.5;
                elements.push(
                    <g key={`del-h-${dc.idx}`} className="conn-handle delete-only" onClick={(e) => { e.stopPropagation(); removeConnection(dc.idx); }}>
                        <line x1={dc.child.x} y1={midY} x2={dc.child.x} y2={dc.child.y - 16} stroke="transparent" strokeWidth="12" style={{ cursor: 'pointer' }} />
                        <circle cx={dc.child.x} cy={handleY} r={8} className="delete-bg" />
                        <text x={dc.child.x} y={handleY + 3} textAnchor="middle" className="delete-icon" fontSize="9">×</text>
                        <title>Eliminar descendencia</title>
                    </g>
                );
            });
        });

        return elements;
    };

    if (loading) return <div className="pedigree-loading">Cargando...</div>;

    return (
        <div className="pedigree-clinical">
            <div className="pedigree-toolbar">
                <button className="tool-btn" onClick={openAddModal}><Plus size={16} /> Agregar</button>
                <button className={`tool-btn ${connectMode ? 'active' : ''}`} onClick={toggleConnectMode}><Link2 size={16} /> {connectMode ? (connectFrom ? '2do' : '1ro') : 'Conectar'}</button>
                <div className="toolbar-divider" />
                <button className="tool-btn" onClick={handleUndo} disabled={historyIndex <= 0}><Undo2 size={16} /></button>
                <button className="tool-btn" onClick={handleRedo} disabled={historyIndex >= history.length - 1}><Redo2 size={16} /></button>
                <span className={`save-status save-${saveState}`}>
                    {saveState === 'saving' && 'Guardando...'}
                    {saveState === 'saved' && 'Guardado'}
                    {saveState === 'error' && 'Error al guardar'}
                    {saveState === 'idle' && 'Sin cambios pendientes'}
                </span>
                <div className="toolbar-divider" />
                <button className={`tool-btn ${showFilesPanel ? 'active' : ''}`} onClick={() => setShowFilesPanel(!showFilesPanel)}>
                    <Paperclip size={16} />
                    Adjuntos {pedigreeFiles.length > 0 && <span className="files-badge">{pedigreeFiles.length}</span>}
                </button>
                <div className="toolbar-divider" />
                <button className="tool-btn" onClick={handleZoomOut}><ZoomOut size={16} /></button>
                <span className="zoom-label">{Math.round(zoom * 100)}%</span>
                <button className="tool-btn" onClick={handleZoomIn}><ZoomIn size={16} /></button>
                <button className="tool-btn" onClick={handleZoomReset}><RotateCcw size={14} /></button>
                <div className="legend"><span>◻ H</span><span>○ M</span><span>◼ Afect</span></div>
            </div>

            <div className="pedigree-canvas-container" onMouseDown={startPan} style={{ cursor: panning ? 'grabbing' : 'grab', userSelect: 'none' }}>
                <svg ref={svgRef} className="pedigree-canvas" viewBox={`${panX} ${panY} ${900 / zoom} ${600 / zoom}`} style={{ userSelect: 'none' }}>
                    <rect width="1200" height="900" x="-150" y="-150" fill="#e8e8e8" />
                    <rect x="0" y="0" width="900" height="600" fill="#fff" stroke="#bbb" strokeWidth="2" strokeDasharray="6,3" rx="4" />
                    <text x="450" y="16" textAnchor="middle" fill="#ccc" fontSize="11">Área de trabajo</text>
                    {renderConnections()}
                    {pedigree.members.map(m => (
                        <g key={m.id} transform={`translate(${m.x}, ${m.y})`} className={`pedigree-node ${connectMode ? 'connect-mode' : ''} ${connectFrom === m.id ? 'selected' : ''}`}
                            onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, m); }} onClick={() => handleNodeClick(m)}
                            onMouseEnter={(e) => handleMouseEnter(m, e)} onMouseLeave={handleMouseLeave} style={{ cursor: connectMode ? 'pointer' : 'grab' }}>
                            {renderSymbol(m)}
                            <text y={26} textAnchor="middle" className="node-name" style={{ textShadow: "0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff" }}>{m.name || '?'}</text>
                            {m.age && <text y={38} textAnchor="middle" className="node-age" style={{ textShadow: "0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff" }}>{m.age}</text>}
                            <g className="node-actions">
                                <rect x={-18} y={-32} width={16} height={12} rx={2} fill="white" stroke="#bbb" strokeWidth="0.5" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); openEditModal(m); }} />
                                <text x={-10} y={-23} fontSize="8" fill="#555" textAnchor="middle" style={{ pointerEvents: 'none' }}>✎</text>
                                <rect x={2} y={-32} width={16} height={12} rx={2} fill="white" stroke="#bbb" strokeWidth="0.5" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }} />
                                <text x={10} y={-23} fontSize="8" fill="#c00" textAnchor="middle" style={{ pointerEvents: 'none' }}>✕</text>
                            </g>
                        </g>
                    ))}
                    {pedigree.members.length === 0 && <text x="300" y="225" textAnchor="middle" fill="#999" fontSize="14">Clic en "Agregar"</text>}
                </svg>
            </div>

            <div className="pedigree-hint">Arrastre el fondo para navegar • Snap magnetico activo • Use + en la union para consanguinidad • Use X para eliminar conexiones</div>

            {/* Files Panel (Sidebar overlay) */}
            <div className={`pedigree-files-panel ${showFilesPanel ? 'open' : ''}`}>
                <div className="files-panel-header">
                    <h4>Archivos del Árbol</h4>
                    <button onClick={() => setShowFilesPanel(false)}><X size={18} /></button>
                </div>

                <div className="files-panel-content">
                    <div className="file-upload-zone">
                        <label className={`file-upload-btn ${uploadingFile ? 'disabled' : ''}`}>
                            {uploadingFile ? <Loader2 size={18} className="spin" /> : <UploadCloud size={18} />}
                            <span>{uploadingFile ? 'Subiendo...' : 'Subir Archivo (Max 50MB)'}</span>
                            <input
                                type="file"
                                hidden
                                accept="application/pdf,image/jpeg,image/png,image/gif,image/webp"
                                onChange={handleFileUpload}
                                disabled={uploadingFile}
                            />
                        </label>
                        <div className="file-upload-info">Archivos permitidos: PDF, JPG, PNG, GIF, WEBP</div>
                    </div>

                    <div className="files-list">
                        {loadingFiles ? (
                            <div className="files-loading">Cargando archivos...</div>
                        ) : pedigreeFiles.length === 0 ? (
                            <div className="files-empty">No hay archivos adjuntos.</div>
                        ) : (
                            pedigreeFiles.map(f => (
                                <div key={f.id} className="file-item">
                                    <div className="file-icon">
                                        {(f.file_type || '').includes('pdf') ? <FileText size={20} color="#e11d48" /> : <ImageIcon size={20} color="#0284c7" />}
                                    </div>
                                    <div className="file-info" onClick={() => {
                                        const fileUrl = resolveFileUrl(f.file_path);
                                        if (!fileUrl) {
                                            alert('No se encontro la ruta del archivo.');
                                            return;
                                        }
                                        window.open(fileUrl, '_blank', 'noopener,noreferrer');
                                    }}>
                                        <div className="file-name" title={f.file_name}>{f.file_name}</div>
                                        <div className="file-meta">
                                            {(f.file_size / 1024 / 1024).toFixed(2)} MB • {new Date(f.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <button className="file-delete-btn" title="Eliminar archivo" onClick={() => handleDeleteFile(f.id)}>
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {tooltip && (
                <div className="pedigree-tooltip" style={{ left: tooltip.x + 10, top: tooltip.y + 10 }}>
                    <strong>{tooltip.member.name || 'Sin nombre'}</strong>
                    {tooltip.member.age && <div>Edad: {tooltip.member.age} años</div>}
                    <div>Sexo: {tooltip.member.sex === 'M' ? 'Masculino' : tooltip.member.sex === 'F' ? 'Femenino' : 'Desconocido'}</div>
                    <div>Estado: {tooltip.member.status === 'aborted' ? 'Aborto/Óbito' : tooltip.member.status === 'deceased' ? 'Fallecido' : 'Vivo'}</div>
                    {(tooltip.member.status === 'deceased' || tooltip.member.status === 'aborted') && tooltip.member.causeOfDeath && <div>Causa/Detalle: {tooltip.member.causeOfDeath}</div>}
                    {tooltip.member.affected && <div>⬛ Afectado</div>}
                    {tooltip.member.carrier && <div>● Portador</div>}
                    {tooltip.member.isProband && <div>➤ Probando</div>}
                    {tooltip.member.notes && <div>Notas: {tooltip.member.notes}</div>}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-box">
                        <div className="modal-header"><h4>{editingMember ? 'Editar' : 'Nuevo'}</h4><button onClick={() => setShowModal(false)}><X size={18} /></button></div>
                        <div className="modal-content">
                            <div className="form-grid">
                                <label>Nombre<input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} /></label>
                                <label>Edad<input type="number" value={formData.age} onChange={e => setFormData({ ...formData, age: e.target.value })} /></label>
                            </div>
                            <div className="form-grid">
                                <label>Sexo<select value={formData.sex} onChange={e => setFormData({ ...formData, sex: e.target.value })}><option value="M">Masculino</option><option value="F">Femenino</option><option value="U">Desc.</option></select></label>
                                <label>Estado<select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}><option value="alive">Vivo</option><option value="deceased">Fallecido</option><option value="aborted">Aborto / Óbito</option></select></label>
                            </div>
                            {(formData.status === 'deceased' || formData.status === 'aborted') && <label>Causa / Semanas<input value={formData.causeOfDeath || ''} onChange={e => setFormData({ ...formData, causeOfDeath: e.target.value })} placeholder="Ej: Aborto 8 SDG" /></label>}
                            <div className="form-checks">
                                <label><input type="checkbox" checked={formData.affected} onChange={e => setFormData({ ...formData, affected: e.target.checked })} /> Afectado</label>
                                <label><input type="checkbox" checked={formData.carrier} onChange={e => setFormData({ ...formData, carrier: e.target.checked })} /> Portador</label>
                                <label><input type="checkbox" checked={formData.isProband} onChange={e => setFormData({ ...formData, isProband: e.target.checked })} /> Probando</label>
                            </div>
                            <small style={{ color: '#64748b' }}>Nota: solo puede existir un probando en el arbol.</small>
                            <label>Notas<textarea value={formData.notes || ''} onChange={e => setFormData({ ...formData, notes: e.target.value })} rows="2" /></label>
                        </div>
                        <div className="modal-footer"><button className="btn-cancel" onClick={() => setShowModal(false)}>Cancelar</button><button className="btn-save" onClick={handleSave}><Check size={14} /> Guardar</button></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PedigreeTree;
